/**
 * Chip / panel actions over one job: Cancel, Resume, Restart, Show in folder,
 * Clear finished. Every action fires `feature:storage_transfer_action`
 * ({ action } only — never a name or path). An `invalid_input` refusal of
 * Resume / Restart (state changed meanwhile, `.part` vanished) becomes the
 * inline row notice "No longer resumable" plus a `transfer-list` refresh so the
 * row's `recovery` catches up; any other refusal — including `DestinationBusy`,
 * whose message names the job holding the destination — is an error toast.
 */
import { useCallback } from 'react';
import { toast } from '../../../components/ui/toaster';
import type { StorageIpcResult } from '../../../types/azureStorageModel';
import type { TransferCollisionDecision, TransferJob } from '../../../types/azureStorageTransfers';
import { useTransfersStore, type TransferPendingAction } from '../store/transfersStore';
import { cancelTransfer, clearFinishedTransfers, decideTransferCollision, restartTransfer, resumeTransfer, retryTransferItems, revealTransferFile, trackTransferEvent } from '../services/transfersApi';
import { refreshTransfers } from './useTransferEvents';

export const NO_LONGER_RESUMABLE = 'No longer resumable';

export type TransferActionName = 'cancel' | 'resume' | 'restart' | 'retry_item' | 'decide' | 'clear' | 'open_folder' | 'copy_report' | 'toggle_errors' | 'panel_toggle' | 'panel_open' | 'collision_open' | 'collision_cancel';

type ActionExtra = Record<string, string | boolean>;
function trackAction(action: TransferActionName, job?: TransferJob, extra: ActionExtra = {}): void {
  trackTransferEvent('feature:storage_transfer_action', job
    ? { action, kind: job.kind, origin: job.origin, scope: job.scope, ...extra }
    : { action, ...extra });
}

/** Review A3: how a collision decision was made — `applyToRest` set the job's rule, `auto` = the rule answered it. */
export type CollisionDecisionMeta = { applyToRest?: boolean; auto?: boolean };

async function runRecovery(jobId: string, action: Extract<TransferPendingAction, 'resume' | 'restart'>, call: (jobId: string) => Promise<StorageIpcResult<{ attempt: number }>>): Promise<void> {
  const store = useTransfersStore.getState();
  if (store.pending[jobId]) return;
  trackAction(action, store.jobs[jobId]);
  store.setPending(jobId, action);
  store.setRowNotice(jobId, undefined);
  try {
    const result = await call(jobId);
    if (result.ok) return;
    // A busy destination is not "no longer resumable": the job still is, once the named holder finishes (the message says which).
    if (result.error.category === 'invalid_input' && result.error.code !== 'DestinationBusy') {
      useTransfersStore.getState().setRowNotice(jobId, NO_LONGER_RESUMABLE);
      await refreshTransfers();
      return;
    }
    toast.error({ title: action === 'resume' ? 'Resume failed' : 'Restart failed', description: result.error.message });
  } finally {
    useTransfersStore.getState().setPending(jobId, undefined);
  }
}

/**
 * INT-01: the drawer removes exactly the jobs main cleared (a job main skipped or kept stays, with its errors and offers);
 * then the list is re-read, so a row another window already cleared in main goes too and a failed clear shows main's state.
 */
export async function clearFinishedJobs(scope: 'finished' | 'unfinished' = 'finished'): Promise<void> {
  trackAction('clear', undefined, { scope });
  const result = await clearFinishedTransfers(scope);
  if (result.ok) useTransfersStore.getState().clearFinished(result.value.cleared);
  else toast.error({ title: 'Clear failed', description: result.error.message });
  await refreshTransfers();
}

export function useTransferActions() {
  const cancel = useCallback(async (jobId: string) => {
    trackAction('cancel', useTransfersStore.getState().jobs[jobId]);
    const result = await cancelTransfer(jobId);
    if (!result.ok && result.error.category !== 'invalid_input') toast.error({ title: 'Cancel failed', description: result.error.message });
  }, []);

  const resume = useCallback((jobId: string) => runRecovery(jobId, 'resume', resumeTransfer), []);
  const restart = useCallback((jobId: string) => runRecovery(jobId, 'restart', restartTransfer), []);

  const retryItems = useCallback(async (jobId: string, items: string[]) => {
    const store = useTransfersStore.getState();
    trackAction('retry_item', store.jobs[jobId]);
    const result = await retryTransferItems(jobId, items);
    if (!result.ok) toast.error({ title: 'Retry failed', description: result.error.message });
  }, []);

  const decide = useCallback(async (jobId: string, item: string, decision: TransferCollisionDecision['action'], destination?: string, meta: CollisionDecisionMeta = {}) => {
    const store = useTransfersStore.getState();
    trackAction('decide', store.jobs[jobId], { decision, applyToRest: meta.applyToRest === true, auto: meta.auto === true });
    if (meta.applyToRest) store.setCollisionRule(jobId, { action: decision, setOn: item });
    const result = await decideTransferCollision(jobId, item, decision, destination);
    if (!result.ok) {
      if (meta.applyToRest || meta.auto) useTransfersStore.getState().setCollisionRule(jobId, undefined);
      toast.error({ title: 'Collision decision failed', description: result.error.message });
    }
    return result.ok;
  }, []);

  /** Review A3: Cancel from the collision prompt (button or Escape); the prompt keeps an inline error when it fails. */
  const cancelFromCollision = useCallback(async (jobId: string, via: 'button' | 'escape'): Promise<{ ok: true } | { ok: false; message: string }> => {
    trackAction('collision_cancel', useTransfersStore.getState().jobs[jobId], { via });
    const result = await cancelTransfer(jobId);
    // invalid_input = the job already stopped: its prompt goes with it.
    return result.ok || result.error.category === 'invalid_input' ? { ok: true } : { ok: false, message: result.error.message };
  }, []);

  const openFolder = useCallback((jobId: string, path: string) => {
    trackAction('open_folder', useTransfersStore.getState().jobs[jobId]);
    revealTransferFile(path);
  }, []);

  const clearFinished = useCallback(() => clearFinishedJobs('finished'), []);
  const discardUnfinished = useCallback(() => clearFinishedJobs('unfinished'), []);

  return { cancel, cancelFromCollision, resume, restart, retryItems, decide, openFolder, clearFinished, discardUnfinished };
}

export function trackCopyReport(job: TransferJob): void {
  trackAction('copy_report', job);
}

export function trackErrorToggle(job: TransferJob, open: boolean): void {
  trackTransferEvent('feature:storage_transfer_action', { action: 'toggle_errors', kind: job.kind, origin: job.origin, scope: job.scope, open });
}

export function trackPanelToggle(open: boolean): void {
  trackTransferEvent('feature:storage_transfer_action', { action: 'panel_toggle', open });
}
